import api from './api';

export type OrigenAtributo = 'cliente' | 'articulo';

export interface AtributoValor {
  posicion: number;
  valor: string;
  cantidad: number;
}

export interface AtributoSearchParams {
  search?: string;
  posicion?: number;
  top?: number;
}

const ENDPOINT: Record<OrigenAtributo, string> = {
  cliente: '/api/Clientes/atributos',
  articulo: '/api/Productos/atributos',
};

function normalizeAtributo(raw: unknown): AtributoValor | null {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const posicion = Number(o.posicion ?? o.Posicion);
  const valor = String(o.valor ?? o.Valor ?? '').trim();
  if (!Number.isFinite(posicion) || posicion <= 0 || !valor) return null;
  return { posicion, valor, cantidad: Number(o.cantidad ?? o.Cantidad) || 0 };
}

/** Las ofertas solo usan atributo 1 y 2 de clientes y artículos. */
export const POSICIONES_ATRIBUTO_OFERTA = [1, 2] as const;

const atributoService = {
  /** Valores distintos de atributos cargados en el maestro de clientes o de artículos. */
  getAtributos: async (origen: OrigenAtributo, params: AtributoSearchParams = {}): Promise<AtributoValor[]> => {
    const query: Record<string, string | number> = { top: params.top ?? 50 };
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.posicion) query.posicion = params.posicion;
    const response = await api.get(ENDPOINT[origen], { params: query });
    const data: unknown = response.data;
    const items = Array.isArray(data)
      ? data
      : ((data as Record<string, unknown> | null)?.items ?? (data as Record<string, unknown> | null)?.Items);
    return (Array.isArray(items) ? items : [])
      .map(normalizeAtributo)
      .filter((a): a is AtributoValor => a !== null);
  },

  /** Valores de las posiciones que usan las ofertas, pedidos por posición para que otras no ocupen el tope. */
  getAtributosOferta: async (origen: OrigenAtributo, search?: string, top = 50): Promise<AtributoValor[]> => {
    const porPosicion = await Promise.all(
      POSICIONES_ATRIBUTO_OFERTA.map((posicion) => atributoService.getAtributos(origen, { search, posicion, top })),
    );
    return porPosicion
      .flat()
      .filter((a) => (POSICIONES_ATRIBUTO_OFERTA as readonly number[]).includes(a.posicion))
      .sort((a, b) => b.cantidad - a.cantidad || a.posicion - b.posicion);
  },
};

export default atributoService;
